import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { isOrderNumber, OrderNumberSchema } from "./order-number.js";

const IdentifierSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const MoneyFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const NullableMoneyFenSchema = Type.Union([MoneyFenSchema, Type.Null()]);
const NullableTimestampSchema = Type.Union([
  Type.String({ minLength: 1, maxLength: 64 }),
  Type.Null(),
]);
const NullableIdentifierSchema = Type.Union([IdentifierSchema, Type.Null()]);

export const MemberLifecyclePolicySchema = Type.Object(
  {
    dormantAfterDays: Type.Integer({ minimum: 1, maximum: 3_650 }),
    lostAfterDays: Type.Integer({ minimum: 2, maximum: 7_300 }),
    configurationVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type MemberLifecyclePolicy = Static<typeof MemberLifecyclePolicySchema>;

export const MemberLifecycleStageSchema = Type.Union([
  Type.Literal("ACTIVE"),
  Type.Literal("DORMANT"),
  Type.Literal("LOST"),
  Type.Literal("UNAVAILABLE"),
]);
export type MemberLifecycleStage = Static<typeof MemberLifecycleStageSchema>;

export const MemberLifecycleTimelineKindSchema = Type.Union([
  Type.Literal("PROFILE_CHANGE"),
  Type.Literal("RECHARGE"),
  Type.Literal("CONSUMPTION"),
  Type.Literal("REFUND"),
  Type.Literal("ADJUSTMENT"),
  Type.Literal("ORDER_PAYMENT"),
  Type.Literal("ORDER_REFUND"),
]);
export type MemberLifecycleTimelineKind = Static<typeof MemberLifecycleTimelineKindSchema>;

export const MemberLifecycleTimelineItemSchema = Type.Object(
  {
    id: IdentifierSchema,
    kind: MemberLifecycleTimelineKindSchema,
    occurredAt: Type.String({ minLength: 1, maxLength: 64 }),
    summary: Type.String({ minLength: 1, maxLength: 240 }),
    amountFen: NullableMoneyFenSchema,
    balanceAfterFen: NullableMoneyFenSchema,
    orderNumber: Type.Union([OrderNumberSchema, Type.Null()]),
    rechargeId: NullableIdentifierSchema,
    originalTransactionId: NullableIdentifierSchema,
    profileChangeNumber: Type.Union([
      Type.String({ minLength: 1, maxLength: 64, pattern: "^CRM-[0-9]{8}-[0-9A-F]{12}$" }),
      Type.Null(),
    ]),
  },
  { additionalProperties: false },
);
export type MemberLifecycleTimelineItem = Static<typeof MemberLifecycleTimelineItemSchema>;

export const MemberLifecycleReadModelSchema = Type.Object(
  {
    tenantId: IdentifierSchema,
    storeId: IdentifierSchema,
    memberId: IdentifierSchema,
    memberNumber: Type.String({ minLength: 1, maxLength: 64 }),
    memberVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    generatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    timeZone: Type.String({ minLength: 1, maxLength: 80 }),
    businessDayCutover: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
    policy: MemberLifecyclePolicySchema,
    classification: Type.Object(
      {
        stage: MemberLifecycleStageSchema,
        lastActivityAt: NullableTimestampSchema,
        lastActivityBusinessDate: Type.Union([
          Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
          Type.Null(),
        ]),
        daysSinceLastActivity: Type.Union([
          Type.Integer({ minimum: 0, maximum: 1_000_000 }),
          Type.Null(),
        ]),
        reason: Type.String({ minLength: 1, maxLength: 240 }),
      },
      { additionalProperties: false },
    ),
    preferences: Type.Array(Type.String({ minLength: 1, maxLength: 40 }), {
      maxItems: 10,
      uniqueItems: true,
    }),
    funds: Type.Object(
      {
        principalBalanceFen: MoneyFenSchema,
        bonusBalanceFen: MoneyFenSchema,
        availableBalanceFen: MoneyFenSchema,
        totalRechargedFen: MoneyFenSchema,
        totalBonusFen: MoneyFenSchema,
        totalConsumedFen: MoneyFenSchema,
        walletVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      },
      { additionalProperties: false },
    ),
    receivable: Type.Union([
      Type.Object(
        {
          availability: Type.Literal("AVAILABLE"),
          outstandingFen: MoneyFenSchema,
          explanation: Type.String({ minLength: 1, maxLength: 240 }),
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          availability: Type.Literal("NOT_AVAILABLE"),
          outstandingFen: Type.Null(),
          explanation: Type.String({ minLength: 1, maxLength: 240 }),
        },
        { additionalProperties: false },
      ),
    ]),
    timeline: Type.Array(MemberLifecycleTimelineItemSchema, { maxItems: 500 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type MemberLifecycleReadModel = Static<typeof MemberLifecycleReadModelSchema>;

function canonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function validDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function datePartsAt(
  instant: Date,
  timeZone: string,
): {
  date: string;
  minuteOfDay: number;
} | null {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(instant);
    const part = (type: Intl.DateTimeFormatPartTypes): string | undefined =>
      parts.find((candidate) => candidate.type === type)?.value;
    const year = part("year");
    const month = part("month");
    const day = part("day");
    const hour = Number(part("hour"));
    const minute = Number(part("minute"));
    if (!year || !month || !day || !Number.isInteger(hour) || !Number.isInteger(minute))
      return null;
    return { date: `${year}-${month}-${day}`, minuteOfDay: hour * 60 + minute };
  } catch {
    return null;
  }
}

function previousDate(value: string): string | null {
  if (!validDate(value)) return null;
  const instant = new Date(`${value}T00:00:00.000Z`);
  instant.setUTCDate(instant.getUTCDate() - 1);
  return instant.toISOString().slice(0, 10);
}

function businessDateAt(occurredAt: string, timeZone: string, cutover: string): string | null {
  if (!canonicalTimestamp(occurredAt) || !/^(?:[01]\d|2[0-3]):[0-5]\d$/u.test(cutover)) {
    return null;
  }
  const parts = datePartsAt(new Date(occurredAt), timeZone);
  if (!parts) return null;
  const [cutoverHour, cutoverMinute] = cutover.split(":").map(Number);
  const cutoverMinuteOfDay = (cutoverHour ?? 0) * 60 + (cutoverMinute ?? 0);
  return parts.minuteOfDay < cutoverMinuteOfDay ? previousDate(parts.date) : parts.date;
}

function daysBetween(earlier: string, later: string): number | null {
  if (!validDate(earlier) || !validDate(later)) return null;
  const difference =
    (Date.parse(`${later}T00:00:00.000Z`) - Date.parse(`${earlier}T00:00:00.000Z`)) / 86_400_000;
  return Number.isInteger(difference) && difference >= 0 ? difference : null;
}

function expectedStage(days: number, policy: MemberLifecyclePolicy): MemberLifecycleStage {
  if (days >= policy.lostAfterDays) return "LOST";
  if (days >= policy.dormantAfterDays) return "DORMANT";
  return "ACTIVE";
}

function timelineItemIsConsistent(item: MemberLifecycleTimelineItem): boolean {
  if (!canonicalTimestamp(item.occurredAt)) return false;
  if (item.kind === "ORDER_PAYMENT") {
    return (
      item.profileChangeNumber === null &&
      item.amountFen !== null &&
      item.amountFen > 0 &&
      item.balanceAfterFen === null &&
      item.orderNumber !== null &&
      isOrderNumber(item.orderNumber) &&
      item.rechargeId === null &&
      item.originalTransactionId === null
    );
  }
  if (item.kind === "ORDER_REFUND") {
    return (
      item.profileChangeNumber === null &&
      item.amountFen !== null &&
      item.amountFen > 0 &&
      item.balanceAfterFen === null &&
      item.orderNumber !== null &&
      isOrderNumber(item.orderNumber) &&
      item.rechargeId === null &&
      item.originalTransactionId !== null
    );
  }
  if (item.kind === "PROFILE_CHANGE") {
    return (
      item.profileChangeNumber !== null &&
      item.amountFen === null &&
      item.balanceAfterFen === null &&
      item.orderNumber === null &&
      item.rechargeId === null &&
      item.originalTransactionId === null
    );
  }
  if (item.profileChangeNumber !== null || item.balanceAfterFen === null) return false;
  if (item.kind === "RECHARGE") {
    return (
      item.amountFen !== null &&
      item.amountFen > 0 &&
      item.rechargeId !== null &&
      item.orderNumber === null &&
      item.originalTransactionId === null
    );
  }
  if (item.kind === "CONSUMPTION") {
    return (
      item.amountFen !== null &&
      item.amountFen > 0 &&
      item.rechargeId === null &&
      item.originalTransactionId === null &&
      (item.orderNumber === null || isOrderNumber(item.orderNumber))
    );
  }
  if (item.kind === "REFUND") {
    return (
      item.amountFen !== null &&
      item.amountFen > 0 &&
      item.rechargeId === null &&
      item.originalTransactionId !== null &&
      (item.orderNumber === null || isOrderNumber(item.orderNumber))
    );
  }
  return (
    item.amountFen === null &&
    item.orderNumber === null &&
    item.rechargeId === null &&
    item.originalTransactionId === null
  );
}

export function isMemberLifecycleReadModel(
  value: unknown,
  expectedMemberId?: string,
  expectedScope?: { tenantId: string; storeId: string },
): value is MemberLifecycleReadModel {
  if (!Value.Check(MemberLifecycleReadModelSchema, value)) return false;
  if (expectedMemberId !== undefined && value.memberId !== expectedMemberId) return false;
  if (
    expectedScope !== undefined &&
    (value.tenantId !== expectedScope.tenantId || value.storeId !== expectedScope.storeId)
  ) {
    return false;
  }
  if (
    value.policy.lostAfterDays <= value.policy.dormantAfterDays ||
    !canonicalTimestamp(value.generatedAt) ||
    businessDateAt(value.generatedAt, value.timeZone, value.businessDayCutover) !==
      value.businessDate ||
    value.funds.availableBalanceFen !==
      value.funds.principalBalanceFen + value.funds.bonusBalanceFen ||
    !Number.isSafeInteger(value.funds.availableBalanceFen)
  ) {
    return false;
  }
  const classification = value.classification;
  if (classification.lastActivityAt === null) {
    if (
      classification.stage !== "UNAVAILABLE" ||
      classification.lastActivityBusinessDate !== null ||
      classification.daysSinceLastActivity !== null
    ) {
      return false;
    }
  } else {
    if (
      classification.stage === "UNAVAILABLE" ||
      classification.lastActivityBusinessDate === null ||
      classification.daysSinceLastActivity === null ||
      businessDateAt(classification.lastActivityAt, value.timeZone, value.businessDayCutover) !==
        classification.lastActivityBusinessDate ||
      daysBetween(classification.lastActivityBusinessDate, value.businessDate) !==
        classification.daysSinceLastActivity ||
      expectedStage(classification.daysSinceLastActivity, value.policy) !== classification.stage ||
      Date.parse(classification.lastActivityAt) > Date.parse(value.generatedAt)
    ) {
      return false;
    }
  }
  const preferenceKeys = value.preferences.map((preference) =>
    preference.trim().toLocaleLowerCase("zh-CN"),
  );
  if (
    value.preferences.some((preference) => preference !== preference.trim()) ||
    new Set(preferenceKeys).size !== preferenceKeys.length
  ) {
    return false;
  }
  const ids = new Set<string>();
  const timelineById = new Map<string, MemberLifecycleTimelineItem>();
  let previousOccurredAt = Number.POSITIVE_INFINITY;
  for (const item of value.timeline) {
    const occurredAt = Date.parse(item.occurredAt);
    if (
      ids.has(item.id) ||
      occurredAt > previousOccurredAt ||
      occurredAt > Date.parse(value.generatedAt) ||
      !timelineItemIsConsistent(item)
    ) {
      return false;
    }
    ids.add(item.id);
    timelineById.set(item.id, item);
    previousOccurredAt = occurredAt;
  }
  const refundedByOriginal = new Map<string, number>();
  for (const item of value.timeline) {
    if (
      (item.kind !== "REFUND" && item.kind !== "ORDER_REFUND") ||
      item.originalTransactionId === null ||
      item.amountFen === null
    ) {
      continue;
    }
    const original = timelineById.get(item.originalTransactionId);
    const expectedOriginalKind = item.kind === "REFUND" ? "CONSUMPTION" : "ORDER_PAYMENT";
    if (
      original &&
      (original.kind !== expectedOriginalKind ||
        Date.parse(original.occurredAt) > Date.parse(item.occurredAt) ||
        (original.orderNumber !== null &&
          item.orderNumber !== null &&
          original.orderNumber !== item.orderNumber))
    ) {
      return false;
    }
    const refunded = (refundedByOriginal.get(item.originalTransactionId) ?? 0) + item.amountFen;
    if (!Number.isSafeInteger(refunded)) return false;
    refundedByOriginal.set(item.originalTransactionId, refunded);
  }
  for (const [originalId, refunded] of refundedByOriginal) {
    const original = timelineById.get(originalId);
    if (
      original?.amountFen !== null &&
      original?.amountFen !== undefined &&
      refunded > original.amountFen
    ) {
      return false;
    }
  }
  return true;
}
